// moduleAsk (MODULE-ASK-BRIDGE-1) — an installed app asks the workspace
// assistant a question ABOUT something it shows, the way a member does with
// Inspect & ask (functions/aiInspect.js): the context rides the turn
// STRUCTURED (`context.inspected`, the server's INSPECTED line), and the ask
// goes through the composer's own send (homeAiSend), so the drawer, the main
// thread and the member's placement all behave as for a typed question.
//
//   el.call('moduleAsk', prompt, {
//     context: { title, text?, records?: ['<collection>:<id>' | { collection, id }],
//                route? },
//     mode?: 'assistant' | 'dashboard' | 'tools' | 'apps' | 'presentation' | 'fly'
//   }) → Promise<{ ok: true } | { ok: false, error }>
//
// Before this, the DFI Finances app's "Ask more" called homeAiSend directly
// with the context pasted into the prompt text — the model read it as the
// member's words, and nothing named the app or the records it was about.
//
// Records travel as Inspect & ask marks them (`data-inspect-record`,
// "<collection>:<record id>" — the server's INSPECTED line prints them as
// "records shown"); an object form is folded into that string.
// Bounded like a pick: prompt 2,000 chars, title 120, text 2,000, records 20.
// It starts a turn (credits), so a read-only verb is refused it
// (moduleReadOnlyCall.js REFUSED). The member's own picks stay; an earlier
// ask from the same app and title is replaced, never stacked.

const PROMPT_MAX = 2000
const TITLE_MAX = 120
const TEXT_MAX = 2000
const RECORDS_MAX = 20
const ASK_BRIDGE_MODES = ['assistant', 'dashboard', 'tools', 'apps', 'presentation', 'fly']

const _str = (v, max) => (typeof v === 'string' ? v.trim().slice(0, max) : '')

const _recordToken = (r) => {
  if (typeof r === 'string') return _str(r, 120)
  if (!r || typeof r !== 'object') return ''
  const collection = _str(r.collection, 80)
  const id = _str(String(r.id ?? ''), 120)
  return collection && id ? `${collection}:${id}`.slice(0, 120) : ''
}

const _askBridgeRecords = (list) =>
  [...new Set((Array.isArray(list) ? list : []).map(_recordToken).filter(Boolean))].slice(
    0,
    RECORDS_MAX
  )

// What a pick needs to know about where the ask comes from: the surface the
// member is on (aiScopeSurface — the same reader Inspect & ask uses).
const _surface = (el, root) => {
  try {
    const sf = el.call('aiScopeSurface', root)
    return sf && typeof sf === 'object' ? sf : null
  } catch (_) {
    return null
  }
}

export const buildModuleAskBridge = (root) =>
  async function moduleAsk(prompt, opts) {
    const el = this
    const text = _str(prompt, PROMPT_MAX)
    if (!text) return { ok: false, error: 'moduleAsk needs a question.' }
    if (!root || typeof root.update !== 'function' || !el || typeof el.call !== 'function') {
      return { ok: false, error: 'No workspace assistant here.' }
    }
    const o = opts && typeof opts === 'object' ? opts : {}
    const c = o.context && typeof o.context === 'object' ? o.context : null
    if (c) {
      const title = _str(c.title, TITLE_MAX)
      if (!title) return { ok: false, error: 'moduleAsk context needs a title.' }
      const surface = _surface(el, root)
      const facts = { id: 'ask-' + Date.now().toString(36), title, kind: 'section' }
      const route = _str(c.route, 240) || _str(surface && surface.path, 240)
      if (route) facts.route = route
      if (surface && surface.app && surface.app.module && surface.app.id) {
        facts.app = {
          id: _str(surface.app.id, 120),
          ...(surface.app.name ? { name: _str(surface.app.name, 120) } : {})
        }
      }
      const body = _str(c.text, TEXT_MAX)
      if (body) facts.text = body
      const records = _askBridgeRecords(c.records)
      if (records.length) facts.records = records
      const entry = {
        keyPath: [title],
        key: title,
        tag: 'region',
        title,
        text: (body || title).slice(0, 80),
        pickedAt: Date.now(),
        inspect: facts
      }
      const keep = (Array.isArray(root.pickedElements) ? root.pickedElements : []).filter(
        (p) =>
          !(
            p &&
            p.inspect &&
            p.inspect.title === facts.title &&
            (p.inspect.app && p.inspect.app.id) === (facts.app && facts.app.id)
          )
      )
      root.update({ pickedElements: [...keep, entry] })
    }
    try {
      await el.call(
        'homeAiSend',
        text,
        null,
        0,
        null,
        ASK_BRIDGE_MODES.includes(o.mode) ? { mode: o.mode } : null
      )
      return { ok: true }
    } catch (err) {
      return {
        ok: false,
        error: (err && err.message) || 'The assistant could not take the question.'
      }
    }
  }
